/**
 * Canopy Animation frame selection (SOMEAC).
 *
 * This is a deterministic, side-effect-free selector that maps a telemetry record
 * to a single canopy frame URL under `/canopy-frames/...`.
 *
 * It is designed to be robust to:
 * - snake_case vs camelCase record keys
 * - record "envelope" responses (`{ status, data: record }`)
 * - record payload stored as JSON string vs object (`record.data`)
 */

const DEFAULTS = Object.freeze({
  arrayTravelInches: 276.0,
  tiltMinDeg: -45.0,
  tiltMaxDeg: 45.0,
  staleAfterMs: 60_000,
  // Frame roots (served by SOMEAC frontend, same origin as sac.sabalcanopy.com)
  roots: {
    maintenance: '/canopy-frames/05Status/Maintenance.jpg',
    error: '/canopy-frames/05Status/Error.jpg',
    openingClosingDir: '/canopy-frames/01Opening-Closing',
    sunTrackingDir: '/canopy-frames/03SunTracking',
    unknown: '/canopy-frames/canopy_unknown.jpeg'
  }
});

function toNumberOrNull(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (s === '') return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function pad5(n) {
  return String(n).padStart(5, '0');
}

function safeJsonParse(value) {
  if (typeof value !== 'string') return value;
  const s = value.trim();
  if (s === '') return value;
  // Only attempt to parse likely JSON.
  if (!(s.startsWith('{') || s.startsWith('['))) return value;
  try {
    return JSON.parse(s);
  } catch {
    return value;
  }
}

function pick(obj, keys, fallback = undefined) {
  for (const k of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined && obj[k] !== null) {
      return obj[k];
    }
  }
  return fallback;
}

/**
 * Normalize "wind-hold" stable states to their operator-facing equivalents.
 * - 13 (FlatW)   => 3 (Flat)
 * - 14 (PackedW) => 1 (Packed)
 */
export function normalizeCanopyStateForUi(canopyState) {
  const n = toNumberOrNull(canopyState);
  if (n === 13) return 3;
  if (n === 14) return 1;
  return n;
}

function extractTelemetryRecord(maybeRecord) {
  if (!maybeRecord || typeof maybeRecord !== 'object') return null;
  if (maybeRecord.status === 'error' || maybeRecord.success === false) return null;

  // Unwrap common API envelope: { success: true, data: ... } or { status: 'success', data: <record> }
  const unwrapped = (maybeRecord.success === true && maybeRecord.data && typeof maybeRecord.data === 'object')
    ? maybeRecord.data
    : maybeRecord;
  const envelopeData = unwrapped.status && unwrapped.data && typeof unwrapped.data === 'object'
    ? unwrapped.data
    : null;

  return envelopeData || unwrapped;
}

function extractSabalControllerPayload(record) {
  // record may be telemetry record where record.data or record.payload is JSON string/object payload
  // OR record itself may already be payload.
  if (!record) return {};

  // If it already looks like payload:
  if (record.sabalController) return record;

  // Snake/camel case telemetry record key for payload is typically `data` or `payload`.
  const rawPayload = record.data ?? record.payload;
  const parsedPayload = safeJsonParse(rawPayload);
  if (parsedPayload && typeof parsedPayload === 'object' && parsedPayload.sabalController) return parsedPayload;

  // If record.data is itself a telemetry record (rare), attempt one more unwrap.
  if (parsedPayload && typeof parsedPayload === 'object' && (parsedPayload.data || parsedPayload.payload)) {
    const parsed2 = safeJsonParse(parsedPayload.data ?? parsedPayload.payload);
    if (parsed2 && typeof parsed2 === 'object' && parsed2.sabalController) return parsed2;
  }

  return {};
}

function computeTiltAngleDeg({
  tm1AngleRaw,
  tm2AngleRaw,
  trackingAngleDeg
}) {
  // TM encoder angles are degrees * 100 in telemetry; convert.
  const tm1 = toNumberOrNull(tm1AngleRaw);
  const tm2 = toNumberOrNull(tm2AngleRaw);
  const tm1Deg = tm1 === null ? null : (tm1 / 100);
  const tm2Deg = tm2 === null ? null : (tm2 / 100);

  if (tm1Deg === null && tm2Deg === null) return null;

  const absAvg = (tm1Deg !== null && tm2Deg !== null)
    ? ((Math.abs(tm1Deg) + Math.abs(tm2Deg)) / 2.0)
    : (Math.abs(tm1Deg ?? tm2Deg));

  // Match reference sign behavior:
  // - Prefer tracking angle sign when meaningful.
  // - Else TM1 sign.
  // - Else -TM2 sign (TM2 often mirrored).
  let sign = 1.0;
  if (trackingAngleDeg !== null && Math.abs(trackingAngleDeg) >= 0.5) {
    sign = trackingAngleDeg >= 0 ? 1.0 : -1.0;
  } else if (tm1Deg !== null && Math.abs(tm1Deg) >= 0.1) {
    sign = tm1Deg >= 0 ? 1.0 : -1.0;
  } else if (tm2Deg !== null && Math.abs(tm2Deg) >= 0.1) {
    sign = (-tm2Deg) >= 0 ? 1.0 : -1.0;
  }

  return absAvg * sign;
}

function computeArrayDistanceInches(am1DistanceRaw, am2DistanceRaw) {
  // Array distances are inches * 100 in telemetry; convert.
  const d1 = toNumberOrNull(am1DistanceRaw);
  const d2 = toNumberOrNull(am2DistanceRaw);
  const d1In = d1 === null ? null : (d1 / 100);
  const d2In = d2 === null ? null : (d2 / 100);

  if (d1In === null && d2In === null) return null;
  const avgAbs = (d1In !== null && d2In !== null)
    ? ((Math.abs(d1In) + Math.abs(d2In)) / 2.0)
    : Math.abs(d1In ?? d2In);
  return avgAbs;
}

/**
 * Select a single canopy frame for the given telemetry record.
 *
 * @returns {Object} result
 * @returns {string} result.src - frame URL
 * @returns {boolean} result.isStale - whether the input record is stale/missing
 * @returns {string} result.reason - debug reason/category
 */
export function getCanopyFrameSelection(maybeRecord, options = {}) {
  const cfg = {
    ...DEFAULTS,
    ...options,
    roots: { ...DEFAULTS.roots, ...(options.roots || {}) }
  };

  const record = extractTelemetryRecord(maybeRecord);
  if (!record) {
    return { src: cfg.roots.maintenance, isStale: true, reason: 'no_record' };
  }

  const ts = pick(record, ['timestamp', 'Timestamp', 'controllerTimestamp', 'observedAt', 'observed_at'], null);
  const tsStr = typeof ts === 'string' ? ts : null;
  const tsDate = tsStr ? new Date(tsStr) : null;
  const ageMs = tsDate && !Number.isNaN(tsDate.getTime()) ? (Date.now() - tsDate.getTime()) : null;
  if (!cfg.ignoreStale && ageMs !== null && ageMs > cfg.staleAfterMs) {
    return { src: cfg.roots.maintenance, isStale: true, reason: 'stale_record' };
  }

  const payload = extractSabalControllerPayload(record);
  const sabalController = payload.sabalController || {};
  const systemInfo = sabalController.systemInfo || {};

  // State inputs (unscaled integers)
  const systemState = toNumberOrNull(pick(systemInfo, ['systemState', 'system_state'], null));
  const canopyStateRaw = pick(systemInfo, ['canopyState', 'canopy_state'], null);
  const canopyStateUi = normalizeCanopyStateForUi(canopyStateRaw);

  const doorsObj = pick(sabalController, ['Doors', 'doors'], {}) || {};
  const doorState = toNumberOrNull(pick(doorsObj, ['doorState', 'door_state'], null));

  // Error conditions (exact)
  const isError =
    systemState === 9 ||
    canopyStateUi === 6 ||
    canopyStateUi === 10 ||
    canopyStateUi === 11 ||
    canopyStateUi === 12;
  if (isError) {
    return { src: cfg.roots.error, isStale: false, reason: 'error' };
  }

  // Clean mode => Maintenance image (reference behavior)
  if (canopyStateUi === 4) {
    return { src: cfg.roots.maintenance, isStale: false, reason: 'cleaning' };
  }

  // Tilt/tracking frames
  const tiltObj = pick(sabalController, ['Tilt', 'tilt'], {}) || {};
  const trackingAngleRaw = pick(tiltObj, ['current tracking angle', 'currentTrackingAngle'], null);
  const trackingAngleDeg = (() => {
    const n = toNumberOrNull(trackingAngleRaw);
    return n === null ? null : (n / 100);
  })();

  const tiltMinRaw = pick(tiltObj, ['tilt angle min', 'tiltAngleMin'], null);
  const tiltMaxRaw = pick(tiltObj, ['tilt angle max', 'tiltAngleMax'], null);
  const tiltMinAngleDeg = (() => {
    const n = toNumberOrNull(tiltMinRaw);
    return n === null ? null : (n / 100);
  })();
  const tiltMaxAngleDeg = (() => {
    const n = toNumberOrNull(tiltMaxRaw);
    return n === null ? null : (n / 100);
  })();

  const encoderStatus = pick(sabalController, ['encoderStatus', 'encoders', 'encoder_status'], {}) || {};
  const tm1AngleRaw = pick(encoderStatus?.tm1 || {}, ['angle', 'distance'], null);
  const tm2AngleRaw = pick(encoderStatus?.tm2 || {}, ['angle', 'distance'], null);
  const actualTiltAngleDeg = computeTiltAngleDeg({ tm1AngleRaw, tm2AngleRaw, trackingAngleDeg });

  const sysRotating = 15;
  const canopyTracking = 5;
  const canopyRotating = 9;
  const canopyFlat = 3;
  const canopyReady = 2;

  const shouldUseTiltFrames =
    (canopyStateUi === canopyTracking ||
      canopyStateUi === canopyRotating ||
      canopyStateUi === canopyFlat ||
      canopyStateUi === canopyReady ||
      systemState === sysRotating) &&
    (actualTiltAngleDeg !== null || trackingAngleDeg !== null);

  if (shouldUseTiltFrames) {
    const a = (actualTiltAngleDeg ?? trackingAngleDeg) ?? 0.0;
    let aMin = tiltMinAngleDeg ?? cfg.tiltMinDeg;
    let aMax = tiltMaxAngleDeg ?? cfg.tiltMaxDeg;

    if (aMin === aMax) {
      aMin = cfg.tiltMinDeg;
      aMax = cfg.tiltMaxDeg;
    }
    if (aMin > aMax) {
      const tmp = aMin;
      aMin = aMax;
      aMax = tmp;
    }

    let t = (aMax - a) / (aMax - aMin);
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    const k = 1 + Math.round(t * 89); // 1..90
    const frame = pad5(k);
    return {
      src: `${cfg.roots.sunTrackingDir}/Sun Tracking .${frame}.jpg`,
      isStale: false,
      reason: 'tilt'
    };
  }

  // Opening/Closing frames
  const doorClosed = 1;
  const doorOpen = 2;
  const doorOpening = 3;
  const doorClosing = 4;

  const canopyPacked = 1;
  const canopyUnfolding = 7;
  const canopyFolding = 8;

  const doorNotClosed = doorState !== null && doorState !== doorClosed;
  const doorInMotionOrOpen = doorState === doorOpen || doorState === doorOpening || doorState === doorClosing;

  if (canopyStateUi === canopyPacked && !doorInMotionOrOpen && !doorNotClosed) {
    return {
      src: `${cfg.roots.openingClosingDir}/Opening - Closing .00000.jpg`,
      isStale: false,
      reason: 'openingClosing_packed_doorsClosed'
    };
  }

  if (canopyStateUi === canopyPacked && doorInMotionOrOpen) {
    return {
      src: `${cfg.roots.openingClosingDir}/Opening - Closing .00001.jpg`,
      isStale: false,
      reason: 'openingClosing_packed_doorsOpenOrMoving'
    };
  }

  if (canopyStateUi === canopyFlat || canopyStateUi === canopyReady) {
    return {
      src: `${cfg.roots.openingClosingDir}/Opening - Closing .00015.jpg`,
      isStale: false,
      reason: 'openingClosing_flatOrReady'
    };
  }

  if (canopyStateUi === canopyUnfolding || canopyStateUi === canopyFolding || canopyStateUi === canopyRotating) {
    const am1DistRaw = pick(encoderStatus?.am1 || {}, ['distance'], null);
    const am2DistRaw = pick(encoderStatus?.am2 || {}, ['distance'], null);
    const d = computeArrayDistanceInches(am1DistRaw, am2DistRaw);

    if (d === null || cfg.arrayTravelInches <= 0) {
      return {
        src: `${cfg.roots.openingClosingDir}/Opening - Closing .00002.jpg`,
        isStale: false,
        reason: 'openingClosing_motion_noDistance'
      };
    }

    const ratio = d / cfg.arrayTravelInches;
    let i = Math.round(ratio * 13); // 0..13
    if (i < 0) i = 0;
    if (i > 13) i = 13;

    const frameNum = 2 + i; // 2..15
    const frame = pad5(frameNum);
    return {
      src: `${cfg.roots.openingClosingDir}/Opening - Closing .${frame}.jpg`,
      isStale: false,
      reason: 'openingClosing_motion'
    };
  }

  return { src: cfg.roots.unknown, isStale: false, reason: 'unknown' };
}

